import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PlusCircle, AlertCircle, Trash2, QrCode, Truck, CreditCard, MapPin, CheckCircle2, XCircle, Clock, User, ShoppingCart, Package } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { Mask } from "@/utils/mask";
import { toast } from "sonner";
import { PAYMENT_FORMS, SALE_TYPE_ID } from "@/utils/constants";
import { apiAuth } from "@/Api";
import { getSelectBankAccounts } from "@/Api/bankAccounts";
import { getShowClients } from "@/Api/clients";
import { getSelectPaymentAdvances } from "@/Api/paymentAdvances";
import { AddressSearch } from "@/components/AddressSearch";
import { GoogleMap, loadGoogleMapsScript } from "@/components/GoogleMap";
import { PaymentAdvanceSelectItem } from "@/types/paymentAdvance";

interface PaymentFormItem {
  payment_form_id: number;
  value: number;
  bank_account_id?: string;
  payment_advance_id?: number;
}

interface BankAccount {
  uuid: string;
  id: number;
  surname: string;
  name: string;
}

interface SelectedProduct {
  id: number;
  quantity: number;
}

interface ProductData {
  id: number;
  contest_product_classification: {
    product_name: string;
    lottery: {
      name: string;
      color: string;
    };
    contest: {
      name: string;
    };
    product_classification: {
      name: string;
      sale_price: number;
    };
  };
}

interface PaymentDeliverySelectionProps {
  deliveryMethods: Array<{ id: number; name: string }>;
  paymentForms: Array<{ id: number; name: string }>;
  selectedDeliveryMethod: number;
  selectedRoute: string;
  routes: Array<{ id: number; title: string }>;
  onDeliveryMethodSelect: (id: number) => void;
  onRouteSelect: (id: string) => void;
  selectedPaymentForms?: PaymentFormItem[];
  onPaymentFormsChange: (forms: PaymentFormItem[]) => void;
  totalSaleAmount: number;
  discountAmount?: number;
  selectedClientUuid?: string;
  selectedClientId?: number;
  selectedSellerId?: number;
  selectedClientName?: string;
  selectedProducts?: SelectedProduct[];
  products?: ProductData[];
  address: {
    addressName: string;
    street: string;
    number: string;
    complement?: string;
    reference?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    latitude?: string;
    longitude?: string;
  };
  onAddressChange: (address: {
    addressName: string;
    street: string;
    number: string;
    complement?: string;
    reference?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    latitude?: string;
    longitude?: string;
  }) => void;
}

export const PaymentDeliverySelection = ({
  deliveryMethods,
  paymentForms,
  selectedDeliveryMethod,
  onDeliveryMethodSelect,
  routes,
  selectedRoute,
  onRouteSelect,
  selectedPaymentForms = [],
  onPaymentFormsChange,
  totalSaleAmount,
  discountAmount = 0,
  onAddressChange,
  address,
  selectedClientUuid,
  selectedClientId,
  selectedSellerId,
  selectedClientName,
  selectedProducts = [],
  products = [],
}: PaymentDeliverySelectionProps) => {
  const [newPaymentForm, setNewPaymentForm] = useState<number | null>(null);
  const [newPaymentValue, setNewPaymentValue] = useState<string>("");
  const [remainingAmount, setRemainingAmount] =
    useState<number>(totalSaleAmount);
  const [clientBalance, setClientBalance] = useState<number>(0);
  const [availableBalance, setAvailableBalance] = useState<number>(0);
  const [isLoadingBalance, setIsLoadingBalance] = useState<boolean>(false);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState<string>("");
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState<boolean>(false);
  const [clientHasPurchaseVoucher, setClientHasPurchaseVoucher] = useState<boolean>(false);
  const [clientMaxPurchaseVoucherValue, setClientMaxPurchaseVoucherValue] = useState<number | null>(null);
  const [paymentAdvances, setPaymentAdvances] = useState<PaymentAdvanceSelectItem[]>([]);
  const [selectedPaymentAdvance, setSelectedPaymentAdvance] = useState<string>("");
  const [isLoadingPaymentAdvances, setIsLoadingPaymentAdvances] = useState<boolean>(false);

  useEffect(() => {
    const totalPaid = selectedPaymentForms.reduce(
      (sum, form) => sum + form.value,
      0
    );

    const remaining = Math.round((totalSaleAmount - totalPaid) * 100) / 100;
    setRemainingAmount(remaining);
  }, [selectedPaymentForms, totalSaleAmount]);

  useEffect(() => {
    const balanceBasedForms = selectedPaymentForms.filter(
      (form) =>
        form.payment_form_id == PAYMENT_FORMS.REINVESTMENT
    );

    const usedBalanceAmount = balanceBasedForms.reduce(
      (sum, form) => sum + form.value,
      0
    );

    console.log("Balance-based Forms (Reinvestment):", balanceBasedForms);
    console.log("Used Balance Amount:", usedBalanceAmount);
    console.log("Client Balance:", clientBalance);

    const calculatedAvailableBalance = clientBalance - usedBalanceAmount;
    console.log("Calculated Available Balance:", calculatedAvailableBalance);

    setAvailableBalance(calculatedAvailableBalance);
  }, [clientBalance, selectedPaymentForms]);

  const fetchClientBalance = useCallback(
    async (balanceOriginTypeId?: number) => {
      if (!selectedClientUuid) return;

      setIsLoadingBalance(true);

      try {
        const url = `/clients/${selectedClientUuid}/balance`;
        const params = balanceOriginTypeId
          ? { balance_origin_type_id: balanceOriginTypeId }
          : undefined;

        console.log("Making request to:", url, "with params:", params);

        const { data } = await apiAuth.get(url, { params });
        console.log("Response data:", data);

        if (data?.response !== undefined) {
          const balance = Number(data.response);
          console.log("Setting client balance to:", balance);
          setClientBalance(balance);
        } else {
          console.log("No balance in response, setting to 0");
          setClientBalance(0);
        }
      } catch (error) {
        console.error("Error fetching client balance:", error);
        setClientBalance(0);
      } finally {
        setIsLoadingBalance(false);
      }
    },
    [selectedClientUuid]
  );

  // Buscar o saldo do cliente quando o UUID estiver disponível
  useEffect(() => {
    console.log("UUID:", selectedClientUuid);
    console.log("Payment Form:", newPaymentForm);
    console.log("Selected Payment Forms:", selectedPaymentForms);

    if (selectedClientUuid && selectedClientUuid !== "") {
      // Determinar qual balance_origin_type_id usar
      const balanceOriginTypeId: number | undefined = undefined;

      // Verifica se tem REINVESTMENT ou VALE selecionado
      const hasReinvestmentOrVale =
        newPaymentForm === PAYMENT_FORMS.REINVESTMENT ||
        newPaymentForm === PAYMENT_FORMS.VALE ||
        selectedPaymentForms.some(
          (form) => form.payment_form_id === PAYMENT_FORMS.REINVESTMENT ||
                    form.payment_form_id === PAYMENT_FORMS.VALE
        );

      if (hasReinvestmentOrVale) {
        console.log("Fetching balance (no filter)");
        // Busca o balance total para Reinvestimento e Vale Compra
        fetchClientBalance(balanceOriginTypeId);
      }
    }
  }, [
    selectedClientUuid,
    newPaymentForm,
    selectedPaymentForms,
    fetchClientBalance,
  ]);

  useEffect(() => {
    if (newPaymentForm === PAYMENT_FORMS.DEPOSIT) {
      fetchBankAccounts();
    } else {
      setSelectedBankAccount("");
    }
  }, [newPaymentForm]);

  // Calcula o saldo disponível de Vale Compra
  // Saldo da carteira + limite do vale
  // Ex: saldo carteira 1742.49, limite vale 700 → disponível = 1742.49 + 700 = 2442.49
  // Se saldo negativo: saldo -200, limite 700 → disponível = 700 - 200 = 500
  const availablePurchaseVoucherBalance = clientMaxPurchaseVoucherValue
    ? (Number(clientBalance) || 0) + (Number(clientMaxPurchaseVoucherValue) || 0)
    : 0;

  // Auto-preencher valor quando Vale Compra for selecionado
  useEffect(() => {
    if (newPaymentForm === PAYMENT_FORMS.VALE && clientHasPurchaseVoucher) {
      // Calcula o valor a ser preenchido: o menor entre o saldo disponível e o valor restante
      let valueToFill = remainingAmount;

      if (availablePurchaseVoucherBalance > 0 && availablePurchaseVoucherBalance < remainingAmount) {
        valueToFill = availablePurchaseVoucherBalance;
      }

      if (valueToFill > 0 && availablePurchaseVoucherBalance > 0) {
        setNewPaymentValue(Mask.formatCurrency(valueToFill));
      }
    }
  }, [newPaymentForm, clientHasPurchaseVoucher, availablePurchaseVoucherBalance, remainingAmount]);

  // Fetch client data to get has_purchase_voucher and max_purchase_voucher_value fields
  useEffect(() => {
    const fetchClientData = async () => {
      if (!selectedClientUuid) {
        setClientHasPurchaseVoucher(false);
        setClientMaxPurchaseVoucherValue(null);
        return;
      }

      try {
        const { data } = await getShowClients(selectedClientUuid);
        if (data?.response) {
          setClientHasPurchaseVoucher(data.response.has_purchase_voucher === true);
          setClientMaxPurchaseVoucherValue(data.response.max_purchase_voucher_value || null);
        } else {
          setClientHasPurchaseVoucher(false);
          setClientMaxPurchaseVoucherValue(null);
        }
      } catch (error) {
        console.error("Error fetching client data:", error);
        setClientHasPurchaseVoucher(false);
        setClientMaxPurchaseVoucherValue(null);
      }
    };

    fetchClientData();
  }, [selectedClientUuid]);

  // Fetch payment advances when ADVANCE_PAYMENT is selected
  useEffect(() => {
    if (newPaymentForm === PAYMENT_FORMS.ADVANCE_PAYMENT) {
      fetchPaymentAdvances();
    } else {
      setSelectedPaymentAdvance("");
    }
  }, [newPaymentForm, selectedClientId, selectedSellerId]);

  // Load Google Maps when component mounts
  useEffect(() => {
    const loadMaps = async () => {
      try {
        console.log(
          "PaymentDeliverySelection: Starting to load Google Maps..."
        );
        await loadGoogleMapsScript();
        console.log(
          "PaymentDeliverySelection: Google Maps loaded successfully"
        );
        setIsGoogleMapsLoaded(true);
      } catch (error) {
        console.error(
          "PaymentDeliverySelection: Failed to load Google Maps:",
          error
        );
        setIsGoogleMapsLoaded(false);
      }
    };

    loadMaps();
  }, []);

  const fetchPaymentAdvances = async () => {
    if (!selectedClientId || !selectedSellerId) {
      setPaymentAdvances([]);
      return;
    }

    setIsLoadingPaymentAdvances(true);
    try {
      const { data, ok } = await getSelectPaymentAdvances({
        client_id: selectedClientId,
        seller_id: selectedSellerId,
      });
      if (ok && data?.response) {
        // O backend já retorna apenas PIX disponíveis para uso
        setPaymentAdvances(data.response);
      } else {
        setPaymentAdvances([]);
      }
    } catch (error) {
      console.error("Error fetching payment advances:", error);
      setPaymentAdvances([]);
    } finally {
      setIsLoadingPaymentAdvances(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const { data } = await getSelectBankAccounts();
      if (data?.response) {
        setBankAccounts(data.response);
      }
    } catch (error) {
      console.error("Erro ao buscar contas bancárias:", error);
      toast.error("Erro ao buscar contas bancárias");
    }
  };

  const handleAddPaymentForm = () => {
    if (!newPaymentForm || !newPaymentValue) return;

    // Verificar se precisa de conta bancária para depósito
    if (newPaymentForm === PAYMENT_FORMS.DEPOSIT && !selectedBankAccount) {
      toast.error("Selecione uma conta bancária para depósito");
      return;
    }

    // Verificar se precisa de pix gerado selecionado para ADVANCE_PAYMENT
    if (newPaymentForm === PAYMENT_FORMS.ADVANCE_PAYMENT && !selectedPaymentAdvance) {
      toast.error("Selecione um Pix Gerado");
      return;
    }

    let value: number;

    if (typeof newPaymentValue === "number") {
      value = newPaymentValue;
    } else {
      try {
        value = parseFloat(Mask.transformCurrencyInFloat(newPaymentValue));
        if (isNaN(value) || value <= 0) {
          toast.error("Valor inválido");
          return;
        }
      } catch (error) {
        toast.error("Valor inválido");
        return;
      }
    }

    const tolerance = 0.01;

    if (
      newPaymentForm === PAYMENT_FORMS.REINVESTMENT &&
      value > availableBalance &&
      Math.abs(value - availableBalance) > tolerance
    ) {
      toast.error(
        `Saldo de reinvestimento insuficiente. Disponível: ${Mask.formatCurrency(
          availableBalance
        )}`
      );
      return;
    }

    // Verificar se o valor não excede o valor do pix gerado selecionado
    if (newPaymentForm === PAYMENT_FORMS.ADVANCE_PAYMENT) {
      const selectedAdvance = paymentAdvances.find(
        (advance) => advance.id.toString() === selectedPaymentAdvance
      );
      const advanceAmount = Number(selectedAdvance?.amount || 0);
      if (selectedAdvance && value > advanceAmount) {
        toast.error(
          `O valor não pode exceder o valor do Pix Gerado (${Mask.formatCurrency(
            advanceAmount
          )})`
        );
        return;
      }
    }

    // Verificar se o cliente ainda tem saldo disponível de Vale Compra
    if (newPaymentForm === PAYMENT_FORMS.VALE && availablePurchaseVoucherBalance <= 0) {
      toast.error(
        `O cliente já atingiu o limite de Vale Compra. Saldo na carteira: ${Mask.formatCurrency(
          clientBalance
        )}, Limite do vale: ${Mask.formatCurrency(clientMaxPurchaseVoucherValue || 0)}`
      );
      return;
    }

    // Verificar se o valor não excede o saldo disponível de Vale Compra
    if (
      newPaymentForm === PAYMENT_FORMS.VALE &&
      availablePurchaseVoucherBalance > 0 &&
      value > availablePurchaseVoucherBalance &&
      Math.abs(value - availablePurchaseVoucherBalance) > tolerance
    ) {
      toast.error(
        `O valor não pode exceder o saldo disponível de Vale Compra (${Mask.formatCurrency(
          availablePurchaseVoucherBalance
        )})`
      );
      return;
    }

    if (
      value > remainingAmount &&
      Math.abs(value - remainingAmount) > tolerance
    ) {
      toast.error(
        `O valor não pode exceder o valor restante da venda (${Mask.formatCurrency(
          remainingAmount
        )})`
      );
      return;
    }

    // Arredonda o valor para 2 casas decimais
    const roundedValue = Math.round(value * 100) / 100;

    const paymentFormItem: PaymentFormItem = {
      payment_form_id: newPaymentForm,
      value: roundedValue,
    };
    // Adicionar ID da conta bancária se for depósito
    if (newPaymentForm === PAYMENT_FORMS.DEPOSIT) {
      paymentFormItem.bank_account_id = selectedBankAccount;
    }
    // Adicionar ID do pix gerado se for ADVANCE_PAYMENT
    if (newPaymentForm === PAYMENT_FORMS.ADVANCE_PAYMENT) {
      paymentFormItem.payment_advance_id = parseInt(selectedPaymentAdvance);
    }

    const updatedForms = [...selectedPaymentForms, paymentFormItem];
    onPaymentFormsChange(updatedForms);

    setNewPaymentForm(null);
    setNewPaymentValue("");
    setSelectedBankAccount("");
    setSelectedPaymentAdvance("");
  };

  const handleRemovePaymentForm = (index: number) => {
    const updatedForms = selectedPaymentForms.filter((_, i) => i !== index);
    onPaymentFormsChange(updatedForms);
  };

  const isSpecialSaleType =
    selectedDeliveryMethod === SALE_TYPE_ID.ENDOSSO ||
    selectedDeliveryMethod === SALE_TYPE_ID.SEDEX;

  const availablePaymentForms = (isSpecialSaleType
    ? paymentForms.filter(
        (form) =>
          form.id === PAYMENT_FORMS.PIX ||
          form.id === PAYMENT_FORMS.PIX_BTG ||
          form.id === PAYMENT_FORMS.REINVESTMENT ||
          form.id === PAYMENT_FORMS.VALE ||
          form.id === PAYMENT_FORMS.PIX_MAQUINETA ||
          form.id === PAYMENT_FORMS.DEPOSIT ||
          form.id === PAYMENT_FORMS.PIX_CNPJ ||
          form.id === PAYMENT_FORMS.MONEY ||
          form.id === PAYMENT_FORMS.ADVANCE_PAYMENT
      )
    : paymentForms
  ).filter((form) => {
    // Filter out Vale Compra if client doesn't have has_purchase_voucher
    if (form.id === PAYMENT_FORMS.VALE) {
      return clientHasPurchaseVoucher === true;
    }
    return true;
  });

  // Get IDs of payment advances already used in this sale
  const usedPaymentAdvanceIds = selectedPaymentForms
    .filter((f) => f.payment_form_id === PAYMENT_FORMS.ADVANCE_PAYMENT && f.payment_advance_id)
    .map((f) => f.payment_advance_id);

  // Filter available payment advances (exclude already used ones)
  const availablePaymentAdvances = paymentAdvances.filter(
    (advance) => !usedPaymentAdvanceIds.includes(advance.id)
  );

  // Get selected products with details
  const selectedProductsWithDetails = selectedProducts
    .filter(sp => sp.quantity > 0)
    .map(sp => {
      const product = products.find(p => p.id === sp.id);
      return {
        ...sp,
        product
      };
    });

  return (
    <div className="flex flex-col gap-6 mt-4 p-4">
      <h2 className="text-2xl font-semibold text-center">
        Entrega e Pagamento
      </h2>

      {/* Card de Resumo do Pedido */}
      <Card className="border-2 bg-gradient-to-r from-slate-50 to-gray-50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-3 text-lg">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-indigo-600" />
            </div>
            <span>Resumo do Pedido</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            {/* Info do Cliente */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                <User className="w-4 h-4" />
                <span>Cliente</span>
              </div>
              <div className="bg-white rounded-lg p-3 border">
                <p className="font-semibold text-gray-800 text-lg">
                  {selectedClientName || "Cliente não selecionado"}
                </p>
              </div>
            </div>

            {/* Produtos */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                <Package className="w-4 h-4" />
                <span>Produtos ({selectedProductsWithDetails.length})</span>
              </div>
              <div className="bg-white rounded-lg p-3 border max-h-48 overflow-y-auto space-y-3">
                {selectedProductsWithDetails.length > 0 ? (
                  selectedProductsWithDetails.map((item) => (
                    <div
                      key={item.id}
                      className="flex justify-between items-start p-2 rounded-lg border-l-4"
                      style={{ borderLeftColor: item.product?.contest_product_classification?.lottery?.color || '#6B7280' }}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-xs font-bold px-2 py-0.5 rounded text-white"
                            style={{ backgroundColor: item.product?.contest_product_classification?.lottery?.color || '#6B7280' }}
                          >
                            {item.product?.contest_product_classification?.lottery?.name}
                          </span>
                          <span className="text-sm text-gray-600">
                            {item.product?.contest_product_classification?.contest?.name}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-800 mt-1">
                          {item.product?.contest_product_classification?.product_name && (
                            <span className="text-blue-600 font-bold mr-1">
                              {item.product?.contest_product_classification?.product_name} -
                            </span>
                          )}
                          {item.product?.contest_product_classification?.product_classification?.name || ""}
                        </p>
                      </div>
                      <span className="font-bold text-gray-900 bg-gray-100 px-2 py-1 rounded text-sm">
                        x{item.quantity}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-400 text-sm italic">Nenhum produto selecionado</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-2 hover:shadow-md transition-shadow">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <span>Método de Entrega</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="delivery-method" className="font-medium">Tipo de venda</Label>
              <Select
                value={
                  selectedDeliveryMethod ? String(selectedDeliveryMethod) : ""
                }
                onValueChange={(value) => onDeliveryMethodSelect(Number(value))}
              >
                <SelectTrigger id="delivery-method">
                  <SelectValue placeholder="Selecione o tipo de venda" />
                </SelectTrigger>
                <SelectContent>
                  {deliveryMethods.map((method) => (
                    <SelectItem key={method.id} value={method.id.toString()}>
                      {method.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedDeliveryMethod === SALE_TYPE_ID.DELIVERY && (
              <div className="grid gap-2">
                <Label htmlFor="route">Rota</Label>
                <Select value={selectedRoute} onValueChange={onRouteSelect}>
                  <SelectTrigger id="route">
                    <SelectValue placeholder="Selecione a rota" />
                  </SelectTrigger>
                  <SelectContent>
                    {routes.map((route) => (
                      <SelectItem key={route.id} value={route.id.toString()}>
                        {route.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-2 hover:shadow-md transition-shadow">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-green-600" />
              </div>
              <span>Formas de Pagamento</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isSpecialSaleType && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2 items-start">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <span className="text-amber-700 text-sm">
                  Para este tipo de venda, apenas o pagamento via PIX, PIX
                  Maquineta, PIX CNPJ, Reinvestimento, Dinheiro ou Depósito é permitido.
                </span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="payment-form" className="font-medium">Forma de pagamento</Label>
                <Select
                  value={newPaymentForm?.toString() || ""}
                  onValueChange={(value) => setNewPaymentForm(Number(value))}
                >
                  <SelectTrigger id="payment-form">
                    <SelectValue placeholder="Selecione a forma de pagamento" />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePaymentForms.map((form) => (
                      <SelectItem key={form.id} value={form.id.toString()}>
                        {form.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="payment-value">Valor</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="payment-value"
                    typeMask="currency"
                    value={newPaymentValue}
                    onChange={(e) => {
                      setNewPaymentValue(e.target.value);
                    }}
                    placeholder="Valor"
                    readOnly={newPaymentForm === PAYMENT_FORMS.ADVANCE_PAYMENT && !!selectedPaymentAdvance}
                    className={newPaymentForm === PAYMENT_FORMS.ADVANCE_PAYMENT && selectedPaymentAdvance ? "bg-gray-100 cursor-not-allowed" : ""}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleAddPaymentForm}
                    disabled={
                      !newPaymentForm ||
                      !newPaymentValue ||
                      (newPaymentForm === PAYMENT_FORMS.DEPOSIT &&
                        !selectedBankAccount) ||
                      (newPaymentForm === PAYMENT_FORMS.ADVANCE_PAYMENT &&
                        !selectedPaymentAdvance)
                    }
                  >
                    <PlusCircle className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {newPaymentForm === PAYMENT_FORMS.REINVESTMENT && (
              <div
                className={`text-sm mt-2 ${
                  availableBalance > 0 ? "text-green-600" : "text-amber-600"
                }`}
              >
                {isLoadingBalance
                  ? "Carregando saldo..."
                  : `Saldo disponível para reinvestimento: ${Mask.formatCurrency(
                      availableBalance
                    )} (Saldo total: ${Mask.formatCurrency(clientBalance)})`}
              </div>
            )}

            {newPaymentForm === PAYMENT_FORMS.VALE && clientHasPurchaseVoucher && (
              <div className={`border rounded-lg p-3 mt-2 ${availablePurchaseVoucherBalance <= 0 ? 'bg-red-50 border-red-200' : 'bg-purple-50 border-purple-200'}`}>
                <div className="flex items-center gap-2">
                  <CreditCard className={`w-4 h-4 ${availablePurchaseVoucherBalance <= 0 ? 'text-red-600' : 'text-purple-600'}`} />
                  <span className={`text-sm font-medium ${availablePurchaseVoucherBalance <= 0 ? 'text-red-700' : 'text-purple-700'}`}>
                    Vale Compra
                  </span>
                </div>
                {clientMaxPurchaseVoucherValue ? (
                  <>
                    <p className="text-sm text-gray-600 mt-1">
                      Saldo na carteira: <span className="font-semibold">{isLoadingBalance ? "Carregando..." : Mask.formatCurrency(clientBalance)}</span>
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Limite do vale: <span className="font-semibold">+ {Mask.formatCurrency(clientMaxPurchaseVoucherValue)}</span>
                    </p>
                    <p className={`text-sm mt-1 font-semibold ${availablePurchaseVoucherBalance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      Disponível para usar: {Mask.formatCurrency(Math.max(0, availablePurchaseVoucherBalance))}
                    </p>
                    {availablePurchaseVoucherBalance <= 0 && (
                      <p className="text-sm mt-2 text-red-700 font-medium">
                        Cliente já atingiu o limite de Vale Compra!
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-purple-600 mt-1">
                    Cliente sem limite máximo definido
                  </p>
                )}
              </div>
            )}

            {newPaymentForm === PAYMENT_FORMS.DEPOSIT && (
              <div className="grid gap-2 mt-4">
                <Label htmlFor="bank-account">
                  Conta bancária para depósito
                </Label>
                <Select
                  value={selectedBankAccount}
                  onValueChange={(value) => setSelectedBankAccount(value)}
                >
                  <SelectTrigger id="bank-account">
                    <SelectValue placeholder="Selecione a conta bancária" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((account) => (
                      <SelectItem key={account.uuid} value={account.uuid}>
                        {account?.name} - {account?.surname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {newPaymentForm === PAYMENT_FORMS.ADVANCE_PAYMENT && (
              <div className="grid gap-2 mt-4">
                <Label htmlFor="payment-advance" className="flex items-center gap-2">
                  <QrCode className="h-4 w-4" />
                  Selecione o Pix Gerado
                </Label>
                {isLoadingPaymentAdvances ? (
                  <div className="text-sm text-gray-500">Carregando pix gerados...</div>
                ) : paymentAdvances.length === 0 ? (
                  <div className="text-sm text-amber-600">
                    {!selectedClientId || !selectedSellerId
                      ? "Selecione um cliente e vendedor para ver os Pix Gerados disponíveis"
                      : "Nenhum Pix Gerado disponível para este cliente e vendedor"}
                  </div>
                ) : availablePaymentAdvances.length === 0 ? (
                  <div className="text-sm text-amber-600">
                    Todos os Pix Gerados disponíveis já foram utilizados nesta venda
                  </div>
                ) : (
                  <Select
                    value={selectedPaymentAdvance}
                    onValueChange={(value) => {
                      setSelectedPaymentAdvance(value);
                      const selectedAdvance = availablePaymentAdvances.find(
                        (advance) => advance.id.toString() === value
                      );
                      if (selectedAdvance) {
                        setNewPaymentValue(Mask.formatCurrency(Number(selectedAdvance.amount)));
                      }
                    }}
                  >
                    <SelectTrigger id="payment-advance">
                      <SelectValue placeholder="Selecione o Pix Gerado" />
                    </SelectTrigger>
                    <SelectContent>
                      {availablePaymentAdvances.map((advance) => (
                        <SelectItem key={advance.id} value={advance.id.toString()}>
                          #{advance.id} - {Mask.formatCurrency(Number(advance.amount))}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selectedPaymentAdvance && (
                  <div className="text-sm text-green-600 mt-1">
                    Pix Gerado selecionado: {Mask.formatCurrency(
                      Number(paymentAdvances.find(
                        (advance) => advance.id.toString() === selectedPaymentAdvance
                      )?.amount || 0)
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 bg-gray-50 rounded-lg p-4 space-y-2">
              {discountAmount > 0 && (
                <>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Subtotal:</span>
                    <span>{Mask.formatCurrency(totalSaleAmount + discountAmount)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Desconto:</span>
                    <span>-{Mask.formatCurrency(discountAmount)}</span>
                  </div>
                  <hr className="border-gray-200" />
                </>
              )}
              <div className="flex justify-between text-base font-semibold">
                <span>Valor total:</span>
                <span>{Mask.formatCurrency(totalSaleAmount)}</span>
              </div>
              <div
                className={`flex items-center gap-2 p-3 rounded-lg mt-2 ${
                  remainingAmount < 0
                    ? "bg-red-100 text-red-700"
                    : remainingAmount > 0
                    ? "bg-amber-100 text-amber-700"
                    : "bg-green-100 text-green-700"
                }`}
              >
                {remainingAmount === 0 ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : remainingAmount > 0 ? (
                  <Clock className="w-5 h-5" />
                ) : (
                  <XCircle className="w-5 h-5" />
                )}
                <span className="font-medium">
                  {remainingAmount === 0
                    ? "Pagamento completo"
                    : remainingAmount > 0
                    ? `Falta pagar: ${Mask.formatCurrency(remainingAmount)}`
                    : `Valor excede o total em: ${Mask.formatCurrency(
                        Math.abs(remainingAmount)
                      )}`}
                </span>
              </div>
            </div>

            {selectedPaymentForms.length > 0 && (
              <div className="space-y-3 mt-4">
                <Label className="font-medium">Formas de pagamento selecionadas</Label>
                <div className="space-y-2">
                  {selectedPaymentForms.map((form, index) => {
                    const paymentFormName =
                      paymentForms.find((pf) => pf.id === form.payment_form_id)
                        ?.name || "Forma desconhecida";

                    let bankAccountInfo = "";
                    if (
                      form.payment_form_id === PAYMENT_FORMS.DEPOSIT &&
                      form.bank_account_id
                    ) {
                      const account = bankAccounts.find(
                        (acc) => acc.uuid === form.bank_account_id
                      );
                      if (account) {
                        bankAccountInfo = ` (${account.name})`;
                      }
                    }

                    return (
                      <div
                        key={index}
                        className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex justify-between items-center"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <CreditCard className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <span className="font-medium text-gray-800">
                              {paymentFormName}
                              {bankAccountInfo}
                            </span>
                            <span className="text-blue-600 font-semibold ml-2">
                              {Mask.formatCurrency(form.value)}
                            </span>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="hover:bg-red-100 rounded-full"
                          onClick={() => handleRemovePaymentForm(index)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {(selectedDeliveryMethod === SALE_TYPE_ID.DELIVERY || selectedDeliveryMethod === SALE_TYPE_ID.SEDEX) && (
        <Card className="border-2 hover:shadow-md transition-shadow">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-purple-600" />
              </div>
              <span>{selectedDeliveryMethod === SALE_TYPE_ID.SEDEX ? "Endereço de Envio (Sedex)" : "Endereço de Entrega"}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="address-search" className="font-medium">Buscar endereço</Label>
              <AddressSearch
                onAddressSelect={(selectedAddress) => {
                  onAddressChange({
                    ...address,
                    ...selectedAddress,
                  });
                }}
                placeholder="Digite o endereço para buscar..."
                defaultValue={address.addressName}
                autoSearchOnMount={false}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="complement">Complemento</Label>
                <Input
                  id="complement"
                  type="text"
                  value={address.complement || ""}
                  onChange={(e) =>
                    onAddressChange({ ...address, complement: e.target.value })
                  }
                  placeholder="Complemento"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="reference">Ponto de Referência</Label>
                <Input
                  id="reference"
                  type="text"
                  value={address.reference || ""}
                  onChange={(e) =>
                    onAddressChange({ ...address, reference: e.target.value })
                  }
                  placeholder="Ponto de referência"
                />
              </div>
            </div>

            {address.latitude && address.longitude && (
              <div className="mt-4">
                <div className="text-xs text-gray-500 mb-2 p-2 bg-blue-50 rounded"></div>
                {isGoogleMapsLoaded ? (
                  <GoogleMap
                    latitude={parseFloat(address.latitude)}
                    longitude={parseFloat(address.longitude)}
                    address={address.addressName}
                  />
                ) : (
                  <div className="w-full h-64 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center">
                    <div className="text-center">
                      <span className="text-gray-500">
                        Carregando Google Maps...
                      </span>
                      <p className="text-xs text-gray-400 mt-1">
                        Verificar console para logs
                      </p>
                    </div>
                  </div>
                )}
                <div className="text-sm text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                  <span className="font-medium">Endereço selecionado:</span>{" "}
                  {address.addressName}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
// End of Selection
